// Admin: snapshot the current invitation as a reusable internal template
// (Phase 2.0 Lite). Captures name, description, category (extended list),
// tags, status, optional cover/preview image. Personal text is
// placeholderized server-side.
import React, { useState, useRef } from 'react';
import { X, Sparkles, Upload, Loader2, Plus, Tag as TagIcon } from 'lucide-react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const CATEGORIES = [
  { id: 'Destination Wedding', label: 'Destination' },
  { id: 'Black Tie',           label: 'Black Tie' },
  { id: 'Garden',              label: 'Garden' },
  { id: 'Palace',              label: 'Palace' },
  { id: 'Coastal',             label: 'Coastal' },
  { id: 'Modern Editorial',    label: 'Editorial' },
  { id: 'Minimal',             label: 'Minimal' },
  { id: 'Dinner / Welcome Event', label: 'Dinner' },
  { id: 'RSVP Only',           label: 'RSVP' },
  { id: 'Full Wedding Website',label: 'Full Site' },
  // Keep the original options for back-compat with existing templates.
  { id: 'wedding',             label: 'Wedding' },
  { id: 'engagement',          label: 'Engagement' },
  { id: 'bridal-shower',       label: 'Shower' },
  { id: 'birthday',            label: 'Birthday' },
  { id: 'general',             label: 'General' },
];

const STATUSES = [
  { id: 'draft',           label: 'Draft' },
  { id: 'internal_review', label: 'Internal review' },
  { id: 'approved',        label: 'Approved' },
];

export default function SaveAsTemplateModal({ open, onClose, eventId, token, onSaved }) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState('Destination Wedding');
  const [tags, setTags] = useState([]);
  const [tagInput, setTagInput] = useState('');
  const [status, setStatus] = useState('draft');
  const [coverImage, setCoverImage] = useState('');
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const fileRef = useRef(null);

  if (!open) return null;

  const addTag = () => {
    const t = tagInput.trim();
    if (!t) return;
    if (tags.includes(t)) return;
    setTags((prev) => [...prev, t]);
    setTagInput('');
  };
  const removeTag = (t) => setTags((prev) => prev.filter((x) => x !== t));

  const handleCoverUpload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await axios.post(
        `${API}/api/events/${eventId}/rsvp-page/upload-image`,
        formData,
        { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' } }
      );
      setCoverImage(res.data.image_url || res.data.url);
      toast.success('Cover uploaded');
    } catch {
      toast.error('Cover upload failed');
    } finally {
      setUploading(false);
    }
  };

  const handleSave = async () => {
    if (!name.trim()) {
      toast.error('Please enter a name for the template');
      return;
    }
    setSaving(true);
    try {
      const res = await axios.post(
        `${API}/api/templates`,
        {
          name: name.trim(),
          description: description.trim(),
          category,
          cover_image: coverImage,
          source_event_id: eventId,
          status,
          tags,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const saved = res.data;
      toast.success(`"${saved.name}" saved as ${saved.status}`, {
        description: 'View in Templates →',
        action: {
          label: 'Open',
          onClick: () => window.location.assign('/studio/templates'),
        },
      });
      onSaved?.(saved);
      onClose();
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Failed to save template');
    } finally {
      setSaving(false);
    }
  };

  const coverPreview = coverImage
    ? (coverImage.startsWith('/') ? `${API}${coverImage}` : coverImage)
    : null;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4"
      onClick={onClose}
      data-testid="save-as-template-modal"
    >
      <div
        className="w-full max-w-md max-h-[92vh] overflow-y-auto rounded-2xl bg-white dark:bg-gray-900 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-5 pt-5 pb-3 flex items-start justify-between border-b border-gray-100 dark:border-gray-800 sticky top-0 bg-white dark:bg-gray-900 z-10">
          <div className="flex items-start gap-2.5">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-violet-500 to-fuchsia-500 flex items-center justify-center shadow-sm">
              <Sparkles className="w-4 h-4 text-white" />
            </div>
            <div>
              <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Save as Template</h3>
              <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-0.5">
                Add this design to your <Link to="/studio/templates" className="underline">Template Library</Link>.
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-1 rounded-md hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-400 hover:text-gray-600 transition-colors"
            data-testid="save-template-close-btn"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="p-5 space-y-4">
          <div>
            <label className="block text-[10px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1.5">
              Template Name *
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g., Portuguese Palace"
              className="w-full px-3 py-2 text-sm rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:border-violet-400 focus:ring-1 focus:ring-violet-300 outline-none transition-colors"
              data-testid="save-template-name-input"
            />
          </div>

          <div>
            <label className="block text-[10px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1.5">
              Description
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={2}
              placeholder="Azulejos, ivory marble, golden light."
              className="w-full px-3 py-2 text-sm rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:border-violet-400 focus:ring-1 focus:ring-violet-300 outline-none resize-none transition-colors"
              data-testid="save-template-desc-input"
            />
          </div>

          <div>
            <label className="block text-[10px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1.5">
              Category
            </label>
            <div className="flex gap-1.5 flex-wrap" data-testid="save-template-category">
              {CATEGORIES.map((c) => (
                <button
                  key={c.id}
                  onClick={() => setCategory(c.id)}
                  className={`px-2.5 py-1 rounded-md text-[11px] font-medium transition-all ${
                    category === c.id
                      ? 'bg-violet-100 dark:bg-violet-900/30 text-violet-700 dark:text-violet-300 ring-1 ring-violet-300'
                      : 'bg-gray-50 dark:bg-gray-800 text-gray-600 dark:text-gray-400 hover:bg-gray-100'
                  }`}
                  data-testid={`save-template-cat-${c.id.replace(/\s+/g, '-').toLowerCase()}`}
                >
                  {c.label}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-[10px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1.5">
              Tags
            </label>
            <div className="flex flex-wrap gap-1.5 mb-2" data-testid="save-template-tags">
              {tags.map((t) => (
                <span
                  key={t}
                  className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium bg-violet-50 dark:bg-violet-900/20 text-violet-700 dark:text-violet-300"
                  data-testid={`save-template-tag-${t}`}
                >
                  <TagIcon className="w-2.5 h-2.5" />
                  {t}
                  <button
                    onClick={() => removeTag(t)}
                    className="ml-0.5 -mr-0.5 hover:text-violet-900"
                    aria-label={`Remove tag ${t}`}
                  >
                    <X className="w-2.5 h-2.5" />
                  </button>
                </span>
              ))}
            </div>
            <div className="flex items-center gap-1.5">
              <input
                type="text"
                value={tagInput}
                onChange={(e) => setTagInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') { e.preventDefault(); addTag(); }
                }}
                placeholder="Add tag (Portugal, Azulejo, Ivory…)"
                maxLength={32}
                className="flex-1 px-2.5 py-1.5 text-xs rounded-md border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 text-gray-700 dark:text-gray-200 placeholder:text-gray-400"
                data-testid="save-template-tag-input"
              />
              <button
                onClick={addTag}
                disabled={!tagInput.trim()}
                className="px-2 py-1.5 rounded-md text-[11px] font-medium bg-violet-50 dark:bg-violet-900/20 text-violet-700 dark:text-violet-300 hover:bg-violet-100 disabled:opacity-40"
                data-testid="save-template-tag-add"
              >
                <Plus className="w-3 h-3" />
              </button>
            </div>
          </div>

          <div>
            <label className="block text-[10px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1.5">
              Status
            </label>
            <div className="flex gap-1.5 flex-wrap" data-testid="save-template-status">
              {STATUSES.map((s) => (
                <button
                  key={s.id}
                  onClick={() => setStatus(s.id)}
                  className={`px-2.5 py-1 rounded-md text-[11px] font-medium transition-all ${
                    status === s.id
                      ? 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300 ring-1 ring-emerald-300'
                      : 'bg-gray-50 dark:bg-gray-800 text-gray-600 dark:text-gray-400 hover:bg-gray-100'
                  }`}
                  data-testid={`save-template-status-${s.id}`}
                >
                  {s.label}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-[10px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1.5">
              Preview Image
            </label>
            <div className="flex items-center gap-3">
              <div className="w-16 h-20 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 flex-shrink-0 overflow-hidden flex items-center justify-center">
                {coverPreview ? (
                  <img src={coverPreview} alt="cover" className="w-full h-full object-cover" />
                ) : (
                  <Upload className="w-4 h-4 text-gray-300" />
                )}
              </div>
              <div className="flex flex-col gap-1">
                <label
                  className={`px-3 py-1.5 rounded-lg text-[11px] font-medium cursor-pointer transition-colors text-center ${
                    uploading
                      ? 'bg-gray-100 text-gray-400'
                      : 'bg-violet-50 text-violet-700 hover:bg-violet-100 dark:bg-violet-900/20 dark:text-violet-300'
                  }`}
                  data-testid="save-template-upload-cover-btn"
                >
                  {uploading ? 'Uploading...' : coverImage ? 'Replace' : 'Upload'}
                  <input
                    ref={fileRef}
                    type="file"
                    accept="image/*"
                    className="hidden"
                    disabled={uploading}
                    onChange={(e) => {
                      const f = e.target.files?.[0];
                      if (f) handleCoverUpload(f);
                      e.target.value = '';
                    }}
                  />
                </label>
                {coverImage && (
                  <button
                    onClick={() => setCoverImage('')}
                    className="px-2 py-0.5 rounded text-[10px] text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20"
                    data-testid="save-template-remove-cover-btn"
                  >
                    Remove
                  </button>
                )}
              </div>
            </div>
            <p className="mt-1.5 text-[10px] text-gray-400 dark:text-gray-500">
              Recommended: a 4:5 portrait shot of the design. Shows in the picker grid.
            </p>
          </div>

          <div className="rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800/50 px-3 py-2">
            <p className="text-[11px] text-amber-800 dark:text-amber-200 leading-relaxed">
              <span className="font-medium">Heads up:</span> personal text (couple name, date) is auto-replaced with soft placeholders so the next couple sees prompts, not someone else&apos;s wedding.
            </p>
          </div>
        </div>

        <div className="px-5 py-3.5 border-t border-gray-100 dark:border-gray-800 flex items-center justify-end gap-2 bg-gray-50/50 dark:bg-gray-900/50 sticky bottom-0">
          <button
            onClick={onClose}
            disabled={saving}
            className="px-3.5 py-1.5 rounded-lg text-xs font-medium text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 disabled:opacity-50 transition-colors"
            data-testid="save-template-cancel-btn"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving || !name.trim()}
            className="px-4 py-1.5 rounded-lg text-xs font-medium bg-gradient-to-r from-violet-600 to-fuchsia-600 text-white hover:from-violet-700 hover:to-fuchsia-700 disabled:opacity-50 flex items-center gap-1.5 transition-all"
            data-testid="save-template-submit-btn"
          >
            {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
            {saving ? 'Saving…' : 'Save Template'}
          </button>
        </div>
      </div>
    </div>
  );
}
